import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Image from '@tiptap/extension-image';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import Color from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import Highlight from '@tiptap/extension-highlight';
import { useEffect, useRef, useState, useCallback } from 'react';
import { Button, DropZone, InlineStack, Text } from '@shopify/polaris';
import './TiptapEditor.css';

const TOOLBAR_BTN = {
  padding: '4px 8px',
  border: '1px solid #ccc',
  borderRadius: '4px',
  background: '#fff',
  cursor: 'pointer',
  fontSize: '12px',
  lineHeight: '1.2',
  minWidth: '28px',
  textAlign: 'center'
};

const TOOLBAR_BTN_ACTIVE = {
  ...TOOLBAR_BTN,
  background: '#e0e0e0',
  borderColor: '#999'
};

function ToolbarButton({ editor, command, isActive, label, title }) {
  return (
    <button type="button" style={isActive ? TOOLBAR_BTN_ACTIVE : TOOLBAR_BTN} onClick={command} title={title || label}>
      {label}
    </button>
  );
}

const SEPARATOR = <span style={{ borderLeft: '1px solid #ddd', margin: '0 4px' }} />;

function Toolbar({ editor }) {
  if (!editor) return null;

  const inTable = editor.isActive('table');

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px', padding: '6px 8px', borderBottom: '1px solid #ddd', background: '#fafafa', alignItems: 'center' }}>
      <ToolbarButton editor={editor} label="B" title="Bold" isActive={editor.isActive('bold')} command={() => editor.chain().focus().toggleBold().run()} />
      <ToolbarButton editor={editor} label="I" title="Italic" isActive={editor.isActive('italic')} command={() => editor.chain().focus().toggleItalic().run()} />
      <ToolbarButton editor={editor} label="U" title="Underline" isActive={editor.isActive('underline')} command={() => editor.chain().focus().toggleUnderline().run()} />
      {SEPARATOR}
      <ToolbarButton editor={editor} label="H2" title="Heading 2" isActive={editor.isActive('heading', { level: 2 })} command={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} />
      <ToolbarButton editor={editor} label="H3" title="Heading 3" isActive={editor.isActive('heading', { level: 3 })} command={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} />
      {SEPARATOR}
      <ToolbarButton editor={editor} label="&#8226;" title="Bullet list" isActive={editor.isActive('bulletList')} command={() => editor.chain().focus().toggleBulletList().run()} />
      <ToolbarButton editor={editor} label="1." title="Ordered list" isActive={editor.isActive('orderedList')} command={() => editor.chain().focus().toggleOrderedList().run()} />
      {SEPARATOR}
      <ToolbarButton editor={editor} label="Table" title="Insert table" isActive={inTable} command={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} />
      {inTable && (
        <>
          <ToolbarButton editor={editor} label="+Row" title="Add row after" command={() => editor.chain().focus().addRowAfter().run()} />
          <ToolbarButton editor={editor} label="+Col" title="Add column after" command={() => editor.chain().focus().addColumnAfter().run()} />
          <ToolbarButton editor={editor} label="-Row" title="Delete row" command={() => editor.chain().focus().deleteRow().run()} />
          <ToolbarButton editor={editor} label="-Col" title="Delete column" command={() => editor.chain().focus().deleteColumn().run()} />
          <ToolbarButton editor={editor} label="&#x2716; Table" title="Delete table" command={() => editor.chain().focus().deleteTable().run()} />
        </>
      )}
      {SEPARATOR}
      <ToolbarButton editor={editor} label="L" title="Align left" isActive={editor.isActive({ textAlign: 'left' })} command={() => editor.chain().focus().setTextAlign('left').run()} />
      <ToolbarButton editor={editor} label="C" title="Align center" isActive={editor.isActive({ textAlign: 'center' })} command={() => editor.chain().focus().setTextAlign('center').run()} />
      <ToolbarButton editor={editor} label="R" title="Align right" isActive={editor.isActive({ textAlign: 'right' })} command={() => editor.chain().focus().setTextAlign('right').run()} />
    </div>
  );
}

function resolveImageKeyFromSrc(src, images) {
  if (!src || !Array.isArray(images) || images.length === 0) return null;

  try {
    const parsed = new URL(src, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    const queryKey = parsed.searchParams.get('key');
    if (queryKey && images.some((img) => img.key === queryKey)) return queryKey;
  } catch (err) {
    // Keep fallback matching below.
  }

  const match = images.find((img) => src.includes(img.key) || src.includes(encodeURIComponent(img.key)));
  return match ? match.key : null;
}

function rewriteImageSrcInDoc(node, images, imageUrls) {
  if (!node || typeof node !== 'object') return false;
  let changed = false;

  if (node.type === 'image' && node.attrs?.src) {
    const key = resolveImageKeyFromSrc(node.attrs.src, images);
    const stableUrl = key ? imageUrls[key] : null;
    if (stableUrl && node.attrs.src !== stableUrl) {
      node.attrs = { ...node.attrs, src: stableUrl };
      changed = true;
    }
  }

  if (Array.isArray(node.content)) {
    for (const child of node.content) {
      if (rewriteImageSrcInDoc(child, images, imageUrls)) changed = true;
    }
  }

  return changed;
}

function isWideImage(img) {
  return img?.displayMode === 'wide' || img?.layout === 'wide' || img?.fullWidth === true;
}

export default function TiptapEditor({ content, onChange, disabled, images, onImageUpload, onImageDelete, onImageUpdate, getImageUrl }) {
  const [imageUrls, setImageUrls] = useState({});
  const [uploading, setUploading] = useState(false);
  const skipUpdateRef = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Image,
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      Color,
      TextStyle,
      Highlight.configure({ multicolor: true })
    ],
    content: content ? (typeof content === 'string' ? JSON.parse(content) : content) : '',
    editable: !disabled,
    onUpdate: ({ editor }) => {
      if (!skipUpdateRef.current && onChange) {
        onChange(JSON.stringify(editor.getJSON()));
      }
    }
  });

  // Sync content from props when it changes externally
  useEffect(() => {
    if (!editor) return;
    let parsed;
    try {
      parsed = content ? (typeof content === 'string' ? JSON.parse(content) : content) : { type: 'doc', content: [] };
    } catch (e) {
      parsed = { type: 'doc', content: [{ type: 'paragraph', content: [{ type: 'text', text: String(content) }] }] };
    }
    const currentJSON = JSON.stringify(editor.getJSON());
    const newJSON = JSON.stringify(parsed);
    if (currentJSON !== newJSON) {
      skipUpdateRef.current = true;
      editor.commands.setContent(parsed);
      skipUpdateRef.current = false;
    }
  }, [content, editor]);

  useEffect(() => {
    if (editor) editor.setEditable(!disabled);
  }, [disabled, editor]);

  // Load signed URLs for images
  useEffect(() => {
    if (!images || !getImageUrl) return;
    const loadUrls = async () => {
      const urls = {};
      for (const img of images) {
        if (!imageUrls[img.key]) {
          try {
            const url = await getImageUrl(img.key);
            urls[img.key] = url;
          } catch (e) {
            console.log('Error loading image URL:', e);
          }
        }
      }
      if (Object.keys(urls).length > 0) {
        setImageUrls((prev) => ({ ...prev, ...urls }));
      }
    };
    loadUrls();
  }, [images, getImageUrl]);

  // Normalize old signed URLs to currently resolved image URLs.
  useEffect(() => {
    if (!editor || !images || images.length === 0) return;

    const json = editor.getJSON();
    const mutated = rewriteImageSrcInDoc(json, images, imageUrls);
    if (!mutated) return;

    skipUpdateRef.current = true;
    editor.commands.setContent(json);
    skipUpdateRef.current = false;
  }, [editor, images, imageUrls]);

  const handleDrop = useCallback(
    async (_dropFiles, acceptedFiles) => {
      if (!onImageUpload || acceptedFiles.length === 0) return;
      setUploading(true);
      try {
        await onImageUpload(acceptedFiles[0]);
      } catch (e) {
        console.log('Error uploading image:', e);
      }
      setUploading(false);
    },
    [onImageUpload]
  );

  const insertImageInEditor = useCallback(
    (key) => {
      if (!editor || !imageUrls[key]) return;
      editor.chain().focus().setImage({ src: imageUrls[key] }).run();
    },
    [editor, imageUrls]
  );

  return (
    <div className="tiptap-editor-wrapper" style={{ border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
      <Toolbar editor={editor} />
      <div style={{ padding: '12px', minHeight: '150px' }}>
        <EditorContent editor={editor} />
      </div>

      {(images || onImageUpload) && (
        <div style={{ borderTop: '1px solid #eee', padding: '12px', background: '#fafafa' }}>
          <Text variant="bodySm" as="p" tone="subdued">
            Images {images?.length > 0 ? `(${images.length})` : ''} — Click thumbnail to insert into editor
          </Text>
          <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'flex-start' }}>
            {(images || []).map((img) => (
              <div key={img.key} style={{ position: 'relative', textAlign: 'center' }}>
                <div onClick={() => insertImageInEditor(img.key)} style={{ cursor: 'pointer', border: '1px solid #ddd', borderRadius: '4px', overflow: 'hidden' }}>
                  {imageUrls[img.key] ? (
                    <img src={imageUrls[img.key]} alt={img.filename} style={{ width: '80px', height: '80px', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ width: '80px', height: '80px', background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Text variant="bodySm" as="span" tone="subdued">
                        ...
                      </Text>
                    </div>
                  )}
                </div>
                {onImageDelete && !disabled && (
                  <button
                    type="button"
                    onClick={() => onImageDelete(img.key)}
                    style={{
                      position: 'absolute',
                      top: '-4px',
                      right: '-4px',
                      background: '#e74c3c',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '50%',
                      width: '18px',
                      height: '18px',
                      fontSize: '10px',
                      cursor: 'pointer',
                      lineHeight: '18px',
                      padding: 0
                    }}
                    title="Delete image"
                  >
                    ✕
                  </button>
                )}
                <Text variant="bodySm" as="p" tone="subdued" truncate>
                  {img.filename?.substring(0, 12)}
                </Text>
                {onImageUpdate && !disabled && (
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                      marginTop: '3px',
                      fontSize: '11px',
                      color: '#4b5563',
                      cursor: 'pointer',
                      userSelect: 'none'
                    }}
                    title="Print this image full page width in the quotation PDF"
                  >
                    <input type="checkbox" checked={isWideImage(img)} onChange={(event) => onImageUpdate(img.key, { displayMode: event.target.checked ? 'wide' : 'standard' })} style={{ margin: 0 }} />
                    Wide PDF
                  </label>
                )}
              </div>
            ))}

            {onImageUpload && !disabled && (
              <div style={{ width: '80px' }}>
                <DropZone onDrop={handleDrop} accept="image/*" type="image" allowMultiple={false} variableHeight>
                  <div style={{ padding: '16px 4px', textAlign: 'center' }}>
                    <Text variant="bodySm" as="p" tone="subdued">
                      {uploading ? '...' : '+ Add'}
                    </Text>
                  </div>
                </DropZone>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
